import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { firstValueFrom, Observable } from 'rxjs';
import * as microsoftTeams from '@microsoft/teams-js';
import { TranslateService } from '@ngx-translate/core';

import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { MEETING_EMBED_SPACE_QUERY_PARAMS } from '../common/utils/actions-query-params-util';
import { URLService } from './dynamic-url.service';
import { ThirdPartyLoginService } from './third-party-login.service';
import {
  TeamsSessionRequest,
  ThirdPartyMeetingSessionResponse,
  ThirdPartySsoBootstrapResult,
} from './third-party-sso.types';

@Injectable({
  providedIn: 'root',
})
export class TeamsLoginService {
  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly translate: TranslateService,
    private readonly thirdPartyLoginService: ThirdPartyLoginService,
  ) {}

  /** Teams meeting SSO bootstrap; failures propagate to {@link ThirdPartySsoGuard}. */
  async bootstrapTeamsSso(): Promise<ThirdPartySsoBootstrapResult> {
    await microsoftTeams.app.initialize();
    const teamsContext = await microsoftTeams.app.getContext();
    const meetingId = teamsContext.meeting?.id?.trim();
    if (!meetingId) {
      throw new Error(
        this.translate.instant(
          'Pencil is only available inside a Teams meeting. Open the app from a meeting to continue.',
        ),
      );
    }

    const entraAccessToken = await microsoftTeams.authentication.getAuthToken();
    const meetingDetails = await new Promise<{ details?: { title?: string } } | null>((resolve) => {
      microsoftTeams.meeting.getMeetingDetails((error, response) => {
        resolve(error || !response ? null : (response as { details?: { title?: string } }));
      });
    });
    const { token: firebaseCustomToken, space: spaceId } = await firstValueFrom(
      this.authenticateTeamsUser({
        accessToken: entraAccessToken,
        meetingId,
        meetingTitle: meetingDetails?.details?.title,
      }),
    );

    return {
      firebaseCustomToken,
      continueUrl: this.thirdPartyLoginService.buildSpaceContinueUrl(
        spaceId,
        MEETING_EMBED_SPACE_QUERY_PARAMS,
      ),
    };
  }

  private authenticateTeamsUser(
    body: TeamsSessionRequest,
  ): Observable<ThirdPartyMeetingSessionResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      [ErrorInterceptorSkipHeader]: '',
    });
    const url = `${this.urlService.getDynamicUrl()}/teams/session`;
    return this.http.post<ThirdPartyMeetingSessionResponse>(url, body, { headers });
  }
}
